import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CarouselProps<T> {
  items: T[];
  renderItem: (item: T, idx: number) => React.ReactNode;
  showNav?: boolean;
  isFetchingNextPage?: boolean;
  infiniteScrollLoader?: React.ReactNode;
  infiniteScrollRef?: React.Ref<HTMLDivElement>;
  itemsPerPage?: number;
  slideStep?: number;
  centerContent?: boolean;
}

function Carousel<T>({
  items,
  renderItem,
  showNav = true,
  isFetchingNextPage,
  infiniteScrollLoader,
  infiniteScrollRef,
  itemsPerPage = 5,
  slideStep = 1,
  centerContent = false,
}: CarouselProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate total pages
  const totalPages = Math.ceil((items?.length || 0) / itemsPerPage);

  // Slice items for the current page
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = isMobile ? items : items.slice(startIndex, endIndex);

  const handlePrev = () => {
    setCurrentPage((prevPage) =>
      prevPage > 0 ? Math.max(0, prevPage - slideStep) : prevPage,
    );
  };

  const handleNext = () => {
    setCurrentPage((prevPage) =>
      prevPage < totalPages - 1
        ? Math.min(totalPages - 1, prevPage + slideStep)
        : prevPage,
    );
  };

  // Don't render if no items
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className="relative lg:mb-6">
      <div className="overflow-hidden">
        <div
          className={`flex gap-1 sm:gap-6 transition-transform duration-500 ease-in-out max-lg:overflow-x-auto ${centerContent ? "justify-center" : ""} no-scrollbar`}
          ref={scrollRef}
        >
          {currentItems.map((item, idx) => renderItem(item, idx))}
          {/* Infinite scroll sentinel */}
          {infiniteScrollRef && (
            <div ref={infiniteScrollRef} style={{ height: 1 }} />
          )}
          {isFetchingNextPage && infiniteScrollLoader}
        </div>
      </div>
      {!isMobile && showNav && totalPages > 1 && (
        <>
          {currentPage > 0 && (
            <button
              className="absolute top-[40%] left-0 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md lg:flex hidden border border-[#037A6A]"
              onClick={handlePrev}
              type="button"
              title="Previous slide"
            >
              <span className="sr-only">Previous slide</span>
              <ChevronLeft size={24} color="#037A6A" />
            </button>
          )}
          {currentPage < totalPages - 1 && (
            <button
              className="absolute top-[40%] right-0 transform -translate-y-1/2 bg-white rounded-full border border-[#037A6A] p-2 shadow-md lg:flex hidden"
              onClick={handleNext}
              type="button"
              title="Next slide"
            >
              <span className="sr-only">Next slide</span>
              <ChevronRight size={24} color="#037A6A" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default Carousel;
